/**
 * Revenue Report — invoice-level Sales / Payments / CN Applied / Outstanding
 * grouped by Category, Salesperson, Client Type, Customer, or Credit Note.
 * Includes Zoho sync, month picker, XLSX/PDF/CSV export, and About popover.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import GlobalSyncPill from './GlobalSyncPill';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Download, Loader2, IndianRupee, Wallet, FileWarning, AlertTriangle, TrendingUp, Info, FileSpreadsheet, FileText, ChevronDown, Filter, Ban, Layers, Eraser, CheckCircle2, X } from 'lucide-react';
import { SavedFilters, SpFilter } from '../../components/books/FilterKit';
import InvoiceDetailModal from './InvoiceDetailModal';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (d) => d instanceof Date ? d.toISOString().slice(0, 10) : d;
const fmtInr = (n) => (n == null || isNaN(n)) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
const fmtInrPlain = (n) => (n == null || isNaN(n)) ? 0 : Math.round(n);

// ─── Date presets ───
function computeRange(preset, monthKey) {
  const t = new Date(); const y = t.getFullYear(), m = t.getMonth();
  const fyStartYear = m >= 3 ? y : y - 1;
  const fromISO = (dt) => new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  if (preset === 'this_month') return { from: fromISO(new Date(y, m, 1)), to: fromISO(new Date(y, m + 1, 0)), label: t.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  if (preset === 'last_month') return { from: fromISO(new Date(y, m - 1, 1)), to: fromISO(new Date(y, m, 0)), label: 'Last month' };
  if (preset === 'this_fy') return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31`, label: `FY ${fyStartYear}-${String(fyStartYear + 1).slice(2)}` };
  if (preset === 'last_fy') return { from: `${fyStartYear - 1}-04-01`, to: `${fyStartYear}-03-31`, label: `FY ${fyStartYear - 1}-${String(fyStartYear).slice(2)}` };
  if (preset === 'month' && monthKey) {
    const [yy, mm] = monthKey.split('-').map(Number);
    const s = new Date(yy, mm - 1, 1), e = new Date(yy, mm, 0);
    return { from: fromISO(s), to: fromISO(e), label: s.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  }
  return { from: `${y}-04-01`, to: fromISO(t), label: 'YTD' };
}

// Build list of months from Apr 2025 → current
function buildMonthOptions() {
  const out = [];
  const now = new Date();
  let d = new Date(2025, 3, 1); // Apr 2025
  while (d <= now) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
    out.push({ key, label });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return out.reverse();
}

const TABS = [
  { id: 'category', label: 'By Category', header: 'Category' },
  { id: 'salesperson', label: 'By Salesperson', header: 'Salesperson' },
  { id: 'client_type', label: 'By Client Type', header: 'Client Type' },
  { id: 'customer', label: 'By Customer', header: 'Customer' },
  { id: 'credit_note', label: 'By Credit Note', header: 'Invoice' },
];

const STATUS_OPTS = [{ id: 'all', label: 'All statuses' }, { id: 'paid', label: 'Paid' }, { id: 'partial', label: 'Partially Paid' }, { id: 'unpaid', label: 'Unpaid' }, { id: 'overdue', label: 'Overdue' }];

const ZOHO_ORG = '699857543';
const zohoUrl = (id) => `https://books.zoho.com/app/${ZOHO_ORG}/#/invoices/${id}`;

const KPI = ({ icon: Icon, label, value, tone = 'slate', onClick, testId }) => {
  const tc = { slate: 'bg-slate-100 text-slate-700', emerald: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', rose: 'bg-rose-100 text-rose-700', violet: 'bg-violet-100 text-violet-700' }[tone];
  return (
    <Card
      className={`p-4 border ${onClick ? 'cursor-pointer hover:border-violet-400 hover:shadow-md transition group' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      data-testid={testId}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
    >
      <div className={`w-9 h-9 rounded-lg ${tc} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-0.5">{value}</p>
      {onClick && <p className="text-[10px] text-violet-500 opacity-0 group-hover:opacity-100 transition mt-1">Click to drill down →</p>}
    </Card>
  );
};

// Sync freshness traffic light (green <15m, amber 15-60m, red older)
function syncFreshness(lastSyncIso) {
  if (!lastSyncIso) return { color: 'bg-slate-300', text: 'text-slate-500', label: 'No sync yet' };
  const mins = Math.floor((Date.now() - new Date(lastSyncIso).getTime()) / 60000);
  if (mins < 15) return { color: 'bg-emerald-500', text: 'text-emerald-700', label: `Fresh · ${mins}m ago` };
  if (mins < 60) return { color: 'bg-amber-400', text: 'text-amber-700', label: `${mins}m ago` };
  if (mins < 1440) return { color: 'bg-red-500', text: 'text-red-700', label: `${Math.floor(mins / 60)}h ago` };
  return { color: 'bg-red-600', text: 'text-red-800', label: `${Math.floor(mins / 1440)}d ago` };
}

/**
 * ReconBadge — inline ✓/⚠ badge that verifies the identity
 *     Sales = Payments + Write-off + CN Applied + Outstanding
 * holds at any level (grand total, group, sub-group). Tolerates ₹1 of
 * rounding drift. Hover reveals the exact diff and per-bucket values so
 * you can eyeball where a discrepancy came from.
 */
function ReconBadge({ sales, payments, write_off, cn_applied, outstanding, testId }) {
  const s = Number(sales) || 0;
  const expected = (Number(payments) || 0) + (Number(write_off) || 0) + (Number(cn_applied) || 0) + (Number(outstanding) || 0);
  const diff = s - expected;
  const ok = Math.abs(diff) < 1;
  const title = `Sales = Paid + Write-off + CN + Outstanding\n` +
    `${fmtInrPlain(s)} = ${fmtInrPlain(payments)} + ${fmtInrPlain(write_off)} + ${fmtInrPlain(cn_applied)} + ${fmtInrPlain(outstanding)}\n` +
    `Diff: ${fmtInrPlain(diff)}`;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-300 text-amber-800'}`}
      title={title}
      data-testid={testId}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {ok ? 'Reconciled' : `Off by ${fmtInr(Math.abs(diff))}`}
    </span>
  );
}



export default function RevenueReportPage() {
  const [preset, setPreset] = useState('this_fy');
  const [monthKey, setMonthKey] = useState(() => {
    const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
  });
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [tab, setTab] = useState('category');
  const [data, setData] = useState(null);
  const [cnData, setCnData] = useState(null); // credit-note tab data
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ col: 'sales', dir: 'desc' });
  const [drill, setDrill] = useState({ open: false, group_value: '', title: '', kpiParams: null, invoices: [], loading: false, cnOnly: false });
  const [drillGroup, setDrillGroup] = useState('none'); // none | category | salesperson | client | cn
  const [drillGroup2, setDrillGroup2] = useState('none');
  const [drillExpanded, setDrillExpanded] = useState({});
  // Drill KPI multi-select — money keys (payments/cn/write_off/outstanding)
  // and Zoho status values. Empty = no KPI filter.
  const [drillMoneyKpis, setDrillMoneyKpis] = useState(new Set());
  const [drillStatusKpis, setDrillStatusKpis] = useState(new Set());
  // Per-invoice detail modal (opened when clicking an invoice row inside the drill)
  const [invoiceDetailId, setInvoiceDetailId] = useState(null);
  // Client-side column sort inside the drill invoice list (flat mode only).
  const [drillSort, setDrillSort] = useState({ col: null, dir: null }); // dir: 'asc' | 'desc' | null
  const [spState, setSpState] = useState({ op: 'is in', selected: [] });
  const [excludeRejected, setExcludeRejected] = useState(true);
  const [salespersons, setSalespersons] = useState([]);
  const [about, setAbout] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidData, setVoidData] = useState({ count: 0, total_voided_value: 0, invoices: [] });
  const [exportOpen, setExportOpen] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const monthOpts = useMemo(buildMonthOptions, []);

  const range = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo, label: 'Custom' };
    return computeRange(preset, monthKey);
  }, [preset, monthKey, customFrom, customTo]);

  const params = useMemo(() => {
    const p = { group_by: tab === 'credit_note' ? 'category' : tab, from: range.from, to: range.to };
    if (customerSearch) p.customer_search = customerSearch;
    if (status !== 'all') p.status = status;
    if (spState.selected.length > 0) { p.salesperson_op = spState.op; p.salesperson_names = JSON.stringify(spState.selected); }
    if (!excludeRejected) p.include_rejected = '1';
    return p;
  }, [tab, range, customerSearch, status, spState, excludeRejected]);

  useEffect(() => {
    api.get('/books/metadata').then((r) => setSalespersons(r.data.salespersons || [])).catch(() => {});
  }, []);

  const fresh = syncFreshness(lastSync);

  const loadSyncStatus = useCallback(async () => {
    try {
      const r = await api.get('/books/sync/status');
      setLastSync(r.data?.last_sync_at || null);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'credit_note') {
        const r = await api.get('/books/revenue-report/credit-notes', { params: { from: range.from, to: range.to, customer_search: customerSearch || undefined, status: status !== 'all' ? status : undefined } });
        setCnData(r.data);
      } else {
        const r = await api.get('/books/revenue-report', { params });
        setData(r.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [params, tab, range, customerSearch, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);

  // Load void invoice count in current date window (shown as a small pill)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/books/revenue-report/void-invoices', { params: { from: range.from, to: range.to, customer_search: customerSearch || undefined } });
        setVoidData(r.data);
      } catch { /* ignore */ }
    })();
  }, [range.from, range.to, customerSearch]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/books/sync');
      toast.success(`Synced · ${r.data.invoices_updated} updated, ${r.data.new_invoices} new · ${r.data.api_calls_estimated} Zoho API calls · ${r.data.elapsed_seconds}s`);
      setLastSync(r.data.synced_at);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Sync failed';
      toast.error(detail);
    } finally { setSyncing(false); }
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => (a[sort.col] > b[sort.col] ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1));
    return rows;
  }, [data, sort]);

  const openDrill = async (group_value, cnOnly = false) => {
    setDrillGroup('none'); setDrillGroup2('none'); setDrillExpanded({});
    setDrillMoneyKpis(new Set()); setDrillStatusKpis(new Set());
    setDrillSort({ col: null, dir: null });
    setDrill({ open: true, group_value, title: '', kpiParams: null, invoices: [], loading: true, cnOnly });
    try {
      const r = await api.get('/books/revenue-report/invoices', { params: { ...params, group_value, cn_only: cnOnly ? '1' : undefined } });
      setDrill((d) => ({ ...d, invoices: r.data.invoices || [], loading: false }));
    } catch {
      toast.error('Failed to load invoices'); setDrill((d) => ({ ...d, loading: false }));
    }
  };

  // ─── KPI card drilldowns — all invoices behind each headline metric ───
  const KPI_DRILL = {
    sales: { title: 'Sales — all invoices in range', params: {} },
    payments: { title: 'Payments — invoices with payments received', params: { kpi: 'payments' } },
    write_off: { title: 'Write-off — invoices with a Zoho write-off', params: { kpi: 'write_off' } },
    cn: { title: 'CN Applied — invoices with credit notes', params: { cn_only: '1' } },
    outstanding: { title: 'Outstanding — invoices with open balance', params: { kpi: 'outstanding' } },
    collection: { title: 'Collection % — all invoices (payments vs sales)', params: {} },
  };

  const openKpiDrill = async (type) => {
    const conf = KPI_DRILL[type];
    setDrillGroup('none'); setDrillGroup2('none'); setDrillExpanded({});
    setDrillMoneyKpis(new Set()); setDrillStatusKpis(new Set());
    setDrill({ open: true, group_value: '', title: conf.title, kpiParams: conf.params, invoices: [], loading: true, cnOnly: type === 'cn' });
    try {
      const r = await api.get('/books/revenue-report/invoices', { params: { ...params, group_value: '', limit: 2000, ...conf.params } });
      setDrill((d) => ({ ...d, invoices: r.data.invoices || [], loading: false }));
    } catch {
      toast.error('Failed to load invoices'); setDrill((d) => ({ ...d, loading: false }));
    }
  };

  const toggleCnOnly = async () => {
    const next = !drill.cnOnly;
    setDrill((d) => ({ ...d, cnOnly: next, loading: true }));
    try {
      const r = await api.get('/books/revenue-report/invoices', { params: { ...params, group_value: drill.group_value, ...(drill.kpiParams || {}), cn_only: next ? '1' : undefined } });
      setDrill((d) => ({ ...d, invoices: r.data.invoices || [], loading: false }));
    } catch { setDrill((d) => ({ ...d, loading: false })); }
  };

  const openRejectedDrill = async () => {
    setDrillGroup('none'); setDrillGroup2('none'); setDrillExpanded({});
    setDrillMoneyKpis(new Set()); setDrillStatusKpis(new Set());
    setDrill({ open: true, group_value: '', title: 'Rejected invoices — excluded from all report numbers', kpiParams: { only_rejected: '1' }, invoices: [], loading: true, cnOnly: false });
    try {
      const r = await api.get('/books/revenue-report/invoices', { params: { ...params, group_value: '', limit: 2000, only_rejected: '1' } });
      setDrill((d) => ({ ...d, invoices: r.data.invoices || [], loading: false }));
    } catch {
      toast.error('Failed to load invoices'); setDrill((d) => ({ ...d, loading: false }));
    }
  };

  // ─── Drilldown grouping (client-side, two levels) ───
  const drillKeyOf = (inv, dim) => dim === 'category' ? (inv.cf_branch_name || 'Not mentioned')
    : dim === 'salesperson' ? (inv.salesperson_name || '— Unassigned —')
    : dim === 'client' ? (inv.customer_name || '— Unknown —')
    : dim === 'client_type' ? (inv.cf_client_type || 'Not set')
    : (inv.cn_applied > 0 ? 'With Credit Note' : 'Without Credit Note');

  // Normalise Zoho invoice.status into finer buckets. Zoho only exposes
  // `paid` when balance=0, regardless of HOW the balance was cleared — cash,
  // credit note, or write-off. Split them so managers see how much actually
  // came IN as cash vs. was written off / offset by a credit note.
  const normaliseStatus = (inv) => {
    const s = (inv?.status || '').toLowerCase();
    if (s === 'partially_paid' || s === 'partial') return 'partial';
    if (s === 'overdue') return 'overdue';
    if (s === 'draft') return 'draft';
    if (s === 'sent' || s === 'unpaid' || s === '') return 'unpaid';
    if (s === 'paid' || s === 'cleared' || s === 'cn_cleared' || s === 'written_off' || s === 'settled_mixed') {
      const hasPmt = (inv.payments || 0) > 0.5;
      const hasCn  = (inv.cn_applied || 0) > 0.5;
      const hasWo  = (inv.write_off || 0) > 0.5;
      const clearances = (hasPmt ? 1 : 0) + (hasCn ? 1 : 0) + (hasWo ? 1 : 0);
      if (clearances >= 2)          return 'mixed';        // cash + CN (± write-off)
      if (hasCn && !hasPmt)         return 'cn_cleared';   // 100% offset by credit note
      if (hasWo && !hasPmt)         return 'written_off';  // 100% written off
      return 'paid';                                        // clean cash
    }
    return 'other';
  };

  // KPI aggregates over the *unfiltered* drill.invoices so users can always
  // see what's available; clicking a card filters the table below.
  const drillKpis = useMemo(() => {
    let sales = 0;
    const money = { payments: { count: 0, amount: 0 }, cn: { count: 0, amount: 0 },
                    write_off: { count: 0, amount: 0 }, outstanding: { count: 0, amount: 0 } };
    // Each status bucket tracks total sales value + how it broke down across
    // the 4 money buckets. That way sum-over-status of `cash` equals the
    // Money row's "Payments Received", and same for cn / write_off / outstanding.
    const mkStatus = () => ({ count: 0, amount: 0, cash: 0, cn: 0, write_off: 0, outstanding: 0 });
    const status = { paid: mkStatus(), partial: mkStatus(), unpaid: mkStatus(),
                     overdue: mkStatus(), draft: mkStatus(), cn_cleared: mkStatus(),
                     written_off: mkStatus(), mixed: mkStatus(), other: mkStatus() };
    for (const inv of drill.invoices) {
      const s = inv.sales || 0;
      sales += s;
      if ((inv.payments || 0) > 0) { money.payments.count++; money.payments.amount += inv.payments; }
      if ((inv.cn_applied || 0) > 0) { money.cn.count++; money.cn.amount += inv.cn_applied; }
      if ((inv.write_off || 0) > 0) { money.write_off.count++; money.write_off.amount += inv.write_off; }
      if ((inv.outstanding || 0) > 0) { money.outstanding.count++; money.outstanding.amount += inv.outstanding; }
      const key = normaliseStatus(inv);
      const st = status[key];
      st.count++; st.amount += s;
      st.cash        += (inv.payments || 0);
      st.cn          += (inv.cn_applied || 0);
      st.write_off   += (inv.write_off || 0);
      st.outstanding += (inv.outstanding || 0);
    }
    return { money, status, sales, total_invoices: drill.invoices.length };
  }, [drill.invoices]);

  const hasKpiFilter = drillMoneyKpis.size > 0 || drillStatusKpis.size > 0;

  // Invoice list after applying multi-select KPI filters (OR within each family, AND across).
  const drillFiltered = useMemo(() => {
    if (!hasKpiFilter) return drill.invoices;
    return drill.invoices.filter((inv) => {
      const moneyOk = drillMoneyKpis.size === 0
        || (drillMoneyKpis.has('payments') && (inv.payments || 0) > 0)
        || (drillMoneyKpis.has('cn') && (inv.cn_applied || 0) > 0)
        || (drillMoneyKpis.has('write_off') && (inv.write_off || 0) > 0)
        || (drillMoneyKpis.has('outstanding') && (inv.outstanding || 0) > 0);
      const statusOk = drillStatusKpis.size === 0 || drillStatusKpis.has(normaliseStatus(inv));
      return moneyOk && statusOk;
    });
  }, [drill.invoices, drillMoneyKpis, drillStatusKpis, hasKpiFilter]);

  const toggleMoneyKpi = (k) => setDrillMoneyKpis((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const toggleStatusKpi = (k) => setDrillStatusKpis((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const clearKpiFilters = () => { setDrillMoneyKpis(new Set()); setDrillStatusKpis(new Set()); };

  // Sorted view of the filtered invoices (flat-mode only). If no sort column
  // is picked, keep the natural order from the API.
  const drillFilteredSorted = useMemo(() => {
    if (!drillSort.col || !drillSort.dir) return drillFiltered;
    const dir = drillSort.dir === 'asc' ? 1 : -1;
    const val = (inv) => {
      switch (drillSort.col) {
        case 'invoice_number': return (inv.invoice_number || '').toString();
        case 'date':           return inv.date || '';
        case 'customer_name':  return (inv.customer_name || '').toLowerCase();
        case 'salesperson_name': return (inv.salesperson_name || '').toLowerCase();
        case 'sales':          return inv.sales || 0;
        case 'payments':       return inv.payments || 0;
        case 'write_off':      return inv.write_off || 0;
        case 'cn_applied':     return inv.cn_applied || 0;
        case 'outstanding':    return inv.outstanding || 0;
        case 'status':         return (inv.status || '').toLowerCase();
        default: return 0;
      }
    };
    return [...drillFiltered].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [drillFiltered, drillSort]);

  const toggleDrillSort = (col) => setDrillSort((prev) => {
    if (prev.col !== col) return { col, dir: 'desc' };
    if (prev.dir === 'desc') return { col, dir: 'asc' };
    return { col: null, dir: null };  // third click clears
  });

  const drillGroups = useMemo(() => {
    if (drillGroup === 'none') return null;
    const g2 = drillGroup2 !== 'none' && drillGroup2 !== drillGroup ? drillGroup2 : null;
    const m = new Map();
    for (const inv of drillFiltered) {
      const k1 = drillKeyOf(inv, drillGroup);
      if (!m.has(k1)) m.set(k1, { key: k1, invoices: [], sales: 0, payments: 0, write_off: 0, cn: 0, outstanding: 0, subs: new Map() });
      const g = m.get(k1);
      g.invoices.push(inv);
      g.sales += inv.sales || 0; g.payments += inv.payments || 0; g.write_off += inv.write_off || 0; g.cn += inv.cn_applied || 0; g.outstanding += inv.outstanding || 0;
      if (g2) {
        const k2 = drillKeyOf(inv, g2);
        if (!g.subs.has(k2)) g.subs.set(k2, { key: k2, invoices: [], sales: 0, payments: 0, write_off: 0, cn: 0, outstanding: 0 });
        const s2 = g.subs.get(k2);
        s2.invoices.push(inv);
        s2.sales += inv.sales || 0; s2.payments += inv.payments || 0; s2.write_off += inv.write_off || 0; s2.cn += inv.cn_applied || 0; s2.outstanding += inv.outstanding || 0;
      }
    }
    return [...m.values()].map((g) => ({ ...g, subs: [...g.subs.values()].sort((a, b) => b.sales - a.sales) })).sort((a, b) => b.sales - a.sales);
  }, [drillFiltered, drillGroup, drillGroup2]);

  const drillTotals = useMemo(() => {
    const t = { sales: 0, payments: 0, write_off: 0, cn: 0, outstanding: 0 };
    for (const inv of drillFiltered) {
      t.sales += inv.sales || 0; t.payments += inv.payments || 0; t.write_off += inv.write_off || 0; t.cn += inv.cn_applied || 0; t.outstanding += inv.outstanding || 0;
    }
    return t;
  }, [drillFiltered]);

  const toggleSort = (col) => setSort((s) => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' });
  const t = data?.totals || {};
  const header = TABS.find(x => x.id === tab).header;

  // ─── Export helpers ───
  const filename = (ext) => `revenue-${tab}-${range.from}_${range.to}.${ext}`;
  const filterLine = () => {
    const bits = [`Range: ${range.from} → ${range.to} (${range.label})`];
    if (customerSearch) bits.push(`Customer: ${customerSearch}`);
    if (status !== 'all') bits.push(`Status: ${status}`);
    return bits.join(' · ');
  };

  const getExportRows = () => {
    if (tab === 'credit_note') {
      return {
        cols: ['Invoice', 'Date', 'Customer', 'Category', 'Salesperson', 'Sales', 'Payments', 'Write-off', 'CN Applied', 'Outstanding'],
        rows: (cnData?.invoices || []).map(inv => [inv.invoice_number, inv.date, inv.customer_name, inv.cf_branch_name, inv.salesperson_name || '', fmtInrPlain(inv.sales), fmtInrPlain(inv.payments), fmtInrPlain(inv.write_off), fmtInrPlain(inv.cn_applied), fmtInrPlain(inv.outstanding)]),
        footer: ['GRAND TOTAL', '', '', '', '', fmtInrPlain(cnData?.totals?.sales), '', fmtInrPlain(cnData?.totals?.write_off), fmtInrPlain(cnData?.totals?.cn_applied), ''],
      };
    }
    return {
      cols: [header, 'Invoices', 'Sales', 'Payments', 'Write-off', 'CN Applied', 'Outstanding', 'Collection %'],
      rows: sortedRows.map(r => [r.group, r.invoices, fmtInrPlain(r.sales), fmtInrPlain(r.payments), fmtInrPlain(r.write_off), fmtInrPlain(r.cn_applied), fmtInrPlain(r.outstanding), r.collection_pct]),
      footer: ['GRAND TOTAL', t.invoices, fmtInrPlain(t.sales), fmtInrPlain(t.payments), fmtInrPlain(t.write_off), fmtInrPlain(t.cn_applied), fmtInrPlain(t.outstanding), t.collection_pct],
    };
  };

  const exportCsv = () => {
    const { cols, rows, footer } = getExportRows();
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [cols.map(q).join(','), ...rows.map(r => r.map(q).join(',')), footer.map(q).join(',')];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename('csv'); a.click();
    setExportOpen(false);
  };

  const exportXlsx = () => {
    const { cols, rows, footer } = getExportRows();
    const meta = [['Revenue Report'], [filterLine()], [], cols, ...rows, footer];
    const ws = XLSX.utils.aoa_to_sheet(meta);
    ws['!cols'] = cols.map((_, i) => ({ wch: i === 0 ? 28 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `By ${header}`);
    XLSX.writeFile(wb, filename('xlsx'));
    setExportOpen(false);
  };

  const exportPdf = () => {
    const { cols, rows, footer } = getExportRows();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(14); doc.setTextColor(30, 30, 60);
    doc.text('Revenue Report', 40, 40);
    doc.setFontSize(9); doc.setTextColor(90, 90, 110);
    doc.text(`By ${header}   ·   ${filterLine()}`, 40, 58);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, 40, 72);
    autoTable(doc, {
      startY: 90,
      head: [cols],
      body: rows,
      foot: [footer],
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [124, 58, 237], textColor: 255 },
      footStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
      columnStyles: cols.reduce((a, _, i) => { if (i > 0) a[i] = { halign: 'right' }; return a; }, {}),
    });
    doc.save(filename('pdf'));
    setExportOpen(false);
  };

  // ─── Drill-down PDF export ────────────────────────────────────────────
  // Multi-page executive-style report: cover with big salesperson name +
  // financial summary + status breakdown, then paginated invoice tables,
  // ending with a grand-total page. Design follows the reference mockup —
  // portrait A4, gradient KPI cards, coloured status pills.
  const exportDrillPdf = () => {
    if (!drill.open || drill.loading || drillFiltered.length === 0) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const PW = doc.internal.pageSize.getWidth();      // 595
    const PH = doc.internal.pageSize.getHeight();     // 842
    const M  = 36;                                     // margin
    const CW = PW - M * 2;                             // content width

    // jsPDF's default Helvetica has no glyphs for arrows, en-dashes, or
    // special punctuation — they render as "!'" or garble text kerning.
    // Sanitise every user-visible string that goes into the PDF through
    // this helper. Keep it minimal (ASCII arrows / dashes) so the report
    // still reads well.
    const asc = (s) => String(s ?? '')
      .replace(/→/g, ' to ')
      .replace(/[–—]/g, '-')
      .replace(/·/g, '-')
      .replace(/₹/g, 'Rs ')
      .replace(/[""]/g, '"')
      .replace(/['']/g, "'")
      .replace(/[▲▼]/g, '')
      .replace(/[✓✗♡❤]/g, '');

    const inr = (n) => `Rs ${Math.round(n || 0).toLocaleString('en-IN')}`;
    const pct = (num, den) => (den > 0 ? (100 * num / den).toFixed(1) : '0.0');
    const fmtDDMon = (iso) => {
      if (!iso) return '';
      try {
        return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      } catch { return iso; }
    };

    const k = drillKpis;
    const groupTitle = drill.title || (drill.group_value ? `${header}: ${drill.group_value}` : 'All salespersons');
    // Short label used in the header / footer of every page (falls back to
    // the drill title if the user opened a non-salesperson drill).
    const scopeLabel = drill.group_value || (drill.title || 'Summary');
    const fyLabel = (() => {
      // FY that contains the range end
      const dt = new Date((range.to || range.from) + 'T00:00:00');
      const y  = dt.getFullYear();
      const fyStart = dt.getMonth() >= 3 ? y : y - 1;
      return `FY ${String(fyStart).slice(2)}-${String(fyStart + 1).slice(2)}`;
    })();
    const totalPagesPlaceholder = '{tp}';   // filled at the end
    const now = new Date();
    const genStamp = now.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit' });

    // ─── Reusable header / footer chrome drawn on every page ──────────
    const drawChrome = (pageNo, subtitle, rightLine1, rightLine2) => {
      // Header — icon + title
      doc.setFillColor(237, 233, 254);
      doc.roundedRect(M, 24, 28, 28, 5, 5, 'F');
      doc.setDrawColor(124, 58, 237); doc.setLineWidth(1);
      doc.roundedRect(M, 24, 28, 28, 5, 5, 'S');
      // Building-icon glyph (simple bars)
      doc.setFillColor(124, 58, 237);
      doc.rect(M + 8,  32, 4, 12, 'F');
      doc.rect(M + 14, 32, 4, 12, 'F');
      doc.rect(M + 20, 32, 4, 12, 'F');

      doc.setTextColor(15, 23, 42); doc.setFontSize(15); doc.setFont(undefined, 'bold');
      doc.text('Revenue Report', M + 38, 38);
      doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(subtitle, M + 38, 52);

      // Right stamp
      doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(rightLine1, PW - M, 38, { align: 'right' });
      if (rightLine2) doc.text(rightLine2, PW - M, 52, { align: 'right' });

      // Header underline
      doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.5);
      doc.line(M, 64, PW - M, 64);

      // Footer
      doc.setDrawColor(226, 232, 240);
      doc.line(M, PH - 30, PW - M, PH - 30);
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text(asc(`${scopeLabel}  -  Revenue Report  -  ${fyLabel}`), M, PH - 16);
      doc.text(`Page ${pageNo} of ${totalPagesPlaceholder}`, PW - M, PH - 16, { align: 'right' });
    };

    // ============= COVER PAGE =============
    drawChrome(1, 'Invoice Summary & Details', `Generated on`, genStamp);

    // Salesperson mega heading
    let y = 90;
    doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(header.toUpperCase(), M, y);
    y += 22;
    doc.setFontSize(30); doc.setTextColor(15, 23, 42); doc.setFont(undefined, 'bold');
    doc.text(asc(scopeLabel), M, y);
    doc.setFont(undefined, 'normal'); doc.setFontSize(10); doc.setTextColor(100, 116, 139);
    y += 16;
    doc.text(`${k.total_invoices} invoices     ${range.from}  to  ${range.to}`, M, y);

    // Right tagline card
    doc.setFillColor(245, 243, 255);
    doc.roundedRect(PW - M - 190, 90, 190, 46, 8, 8, 'F');
    doc.setTextColor(124, 58, 237); doc.setFontSize(11); doc.setFont(undefined, 'bold');
    doc.text('Driving Growth', PW - M - 180, 108);
    doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(107, 70, 193);
    doc.text('Building Relationships', PW - M - 180, 124);

    // Financial Summary section
    y += 30;
    doc.setFontSize(13); doc.setTextColor(15, 23, 42); doc.setFont(undefined, 'bold');
    doc.text('Financial Summary', M, y);
    doc.setFont(undefined, 'normal');

    // Four gradient-style KPI cards
    y += 12;
    const cards = [
      { lbl: 'Total Sales',       amt: k.sales,                    cnt: k.total_invoices,        share: 100,
        fill: [237, 233, 254], border: [196, 181, 253], text: [76, 29, 149],  icon: '₹', accent: [124, 58, 237] },
      { lbl: 'Payments Received', amt: k.money.payments.amount,    cnt: k.money.payments.count,  share: +pct(k.money.payments.amount, k.sales),
        fill: [220, 252, 231], border: [167, 243, 208], text: [6, 78, 59],    icon: '↓', accent: [16, 185, 129] },
      { lbl: 'CN Applied',        amt: k.money.cn.amount,          cnt: k.money.cn.count,        share: +pct(k.money.cn.amount, k.sales),
        fill: [255, 228, 230], border: [253, 164, 175], text: [136, 19, 55],  icon: '⊝', accent: [244, 63, 94] },
      { lbl: 'Outstanding',       amt: k.money.outstanding.amount, cnt: k.money.outstanding.count, share: +pct(k.money.outstanding.amount, k.sales),
        fill: [254, 243, 199], border: [253, 224, 71], text: [120, 53, 15],   icon: '△', accent: [217, 119, 6] },
    ];
    const cardW = (CW - 12) / 4;
    const cardH = 76;
    cards.forEach((c, i) => {
      const x = M + i * (cardW + 4);
      doc.setFillColor(...c.fill);
      doc.setDrawColor(...c.border); doc.setLineWidth(0.8);
      doc.roundedRect(x, y, cardW, cardH, 6, 6, 'FD');
      // Accent stripe on top
      doc.setFillColor(...c.accent);
      doc.roundedRect(x, y, cardW, 3, 1.5, 1.5, 'F');
      // Label
      doc.setTextColor(...c.text); doc.setFontSize(8); doc.setFont(undefined, 'bold');
      doc.text(c.lbl.toUpperCase(), x + 10, y + 20);
      // Amount
      doc.setFontSize(15); doc.setFont(undefined, 'bold');
      doc.text(inr(c.amt), x + 10, y + 42);
      // Sub - count / pct
      doc.setFont(undefined, 'normal'); doc.setFontSize(8); doc.setTextColor(...c.text);
      doc.text(`${c.cnt} inv   -   ${c.share}%`, x + 10, y + 62);
    });
    y += cardH + 14;

    // Progress bar — Received / CN / Outstanding
    const barX = M, barY = y, barW = CW, barH = 10;
    const pReceived = k.money.payments.amount / (k.sales || 1);
    const pCn = k.money.cn.amount / (k.sales || 1);
    const pOut = k.money.outstanding.amount / (k.sales || 1);
    // Rounded background
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(barX, barY, barW, barH, 5, 5, 'F');
    // Received segment
    let segX = barX;
    if (pReceived > 0) {
      const segW = barW * Math.min(1, pReceived);
      doc.setFillColor(16, 185, 129);
      doc.roundedRect(segX, barY, segW, barH, 5, 5, 'F');
      segX += segW;
    }
    // CN segment
    if (pCn > 0) {
      const segW = barW * Math.min(1 - pReceived, pCn);
      doc.setFillColor(244, 63, 94);
      doc.rect(segX, barY, segW, barH, 'F');
      segX += segW;
    }
    // Outstanding
    if (pOut > 0) {
      const segW = barW * Math.min(1 - pReceived - pCn, pOut);
      doc.setFillColor(217, 119, 6);
      doc.rect(segX, barY, segW, barH, 'F');
    }
    // Legend below the bar
    doc.setFontSize(8); doc.setTextColor(6, 78, 59);
    doc.text(`${(pReceived * 100).toFixed(1)}% Received`, barX + 2, barY + barH + 12);
    doc.setTextColor(136, 19, 55);
    doc.text(`${(pCn * 100).toFixed(1)}% CN`, barX + barW * 0.5, barY + barH + 12, { align: 'center' });
    doc.setTextColor(120, 53, 15);
    doc.text(`${(pOut * 100).toFixed(1)}% Outstanding`, barX + barW, barY + barH + 12, { align: 'right' });
    y += barH + 22;

    // Adjustments row (write-off)
    doc.setFillColor(248, 250, 252); doc.setDrawColor(226, 232, 240);
    doc.roundedRect(M, y, CW, 26, 4, 4, 'FD');
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(M + 8, y + 6, 14, 14, 3, 3, 'F');
    doc.setTextColor(71, 85, 105); doc.setFontSize(8); doc.setFont(undefined, 'bold');
    doc.text('ADJUSTMENTS', M + 28, y + 16);
    doc.setFont(undefined, 'normal'); doc.setTextColor(30, 41, 59); doc.setFontSize(9);
    const woLine = `Write-off: ${inr(k.money.write_off.amount)}     ${k.money.write_off.count} invoice${k.money.write_off.count === 1 ? '' : 's'}     ${pct(k.money.write_off.amount, k.sales)}% of sales`;
    doc.text(woLine, M + 108, y + 16);
    y += 36;

    // Invoice Status Breakdown
    doc.setFontSize(13); doc.setTextColor(15, 23, 42); doc.setFont(undefined, 'bold');
    doc.text('Invoice Status Breakdown', M, y);
    doc.setFont(undefined, 'normal');
    y += 12;

    const statusCards = [
      { key: 'paid',        lbl: 'Paid (cash)', fill: [220, 252, 231], border: [134, 239, 172], text: [6, 78, 59] },
      { key: 'partial',     lbl: 'Partial',      fill: [254, 243, 199], border: [252, 211, 77],  text: [120, 53, 15] },
      { key: 'unpaid',      lbl: 'Unpaid',       fill: [219, 234, 254], border: [147, 197, 253], text: [30, 64, 175] },
      { key: 'overdue',     lbl: 'Overdue',      fill: [255, 228, 230], border: [253, 164, 175], text: [136, 19, 55] },
      { key: 'cn_cleared',  lbl: 'CN Cleared',   fill: [237, 233, 254], border: [196, 181, 253], text: [76, 29, 149] },
      { key: 'mixed',       lbl: 'Mixed',        fill: [207, 250, 254], border: [103, 232, 249], text: [22, 78, 99] },
      { key: 'written_off', lbl: 'Written off',  fill: [241, 245, 249], border: [203, 213, 225], text: [51, 65, 85] },
      { key: 'draft',       lbl: 'Draft',        fill: [241, 245, 249], border: [203, 213, 225], text: [51, 65, 85] },
      { key: 'other',       lbl: 'Other',        fill: [241, 245, 249], border: [203, 213, 225], text: [51, 65, 85] },
    ].map((s) => ({ ...s, ...k.status[s.key] })).filter((s) => s.count > 0);

    if (statusCards.length) {
      // Cap 5 cards per row so labels + amounts don't overlap. If we have
      // 6-9 buckets, split into two rows.
      const perRow = Math.min(5, statusCards.length);
      const rows = Math.ceil(statusCards.length / perRow);
      const scW = (CW - (perRow - 1) * 6) / perRow;
      const scH = 78;
      statusCards.forEach((c, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        const x = M + col * (scW + 6);
        const yy = y + row * (scH + 6);
        doc.setFillColor(...c.fill); doc.setDrawColor(...c.border); doc.setLineWidth(0.8);
        doc.roundedRect(x, yy, scW, scH, 6, 6, 'FD');
        // Colored dot + label
        doc.setFillColor(...c.border);
        doc.circle(x + 12, y + 14 + row * (scH + 6), 4, 'F');
        doc.setTextColor(...c.text); doc.setFontSize(9); doc.setFont(undefined, 'bold');
        doc.text(c.lbl, x + 22, yy + 17);
        // Big count
        doc.setFontSize(22);
        doc.text(String(c.count), x + 10, yy + 42);
        // Amount + percentage
        doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(...c.text);
        doc.text(inr(c.amount || 0), x + 10, yy + 58);
        doc.setFontSize(8);
        doc.text(`${pct(c.count, k.total_invoices)}% of invoices`, x + 10, yy + 70);
      });
      y += scH * rows + 6 * (rows - 1) + 14;
    }

    // Filter applied banner
    if (hasKpiFilter) {
      const moneyLabels = { payments: 'Payments', cn: 'CN Applied', write_off: 'Write-off', outstanding: 'Outstanding' };
      const statusLabels = { paid: 'Paid (cash)', partial: 'Partial', unpaid: 'Unpaid', overdue: 'Overdue',
                             cn_cleared: 'CN Cleared', mixed: 'Mixed', written_off: 'Written off',
                             draft: 'Draft', other: 'Other' };
      const parts = [
        ...[...drillMoneyKpis].map((k2) => moneyLabels[k2] || k2),
        ...[...drillStatusKpis].map((k2) => statusLabels[k2] || k2),
      ];
      doc.setFillColor(237, 233, 254); doc.setDrawColor(196, 181, 253);
      doc.roundedRect(M, y, CW, 22, 4, 4, 'FD');
      doc.setTextColor(76, 29, 149); doc.setFontSize(8); doc.setFont(undefined, 'bold');
      doc.text('FILTER APPLIED', M + 10, y + 14);
      doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(88, 28, 135);
      doc.text(`${parts.join('  +  ')}     showing ${drillFiltered.length} of ${drill.invoices.length}`,
        M + 88, y + 14);
      y += 30;
    }

    // Bottom note strip
    doc.setFillColor(248, 250, 252); doc.setDrawColor(226, 232, 240);
    doc.roundedRect(M, y, CW, 24, 4, 4, 'FD');
    doc.setTextColor(100, 116, 139); doc.setFontSize(8);
    doc.text(`This report shows all invoices for ${scopeLabel}, including payments, credit notes and outstanding dues.`,
      M + 10, y + 15);

    // ============= INVOICE DETAIL PAGES =============
    const PAGE_SIZE = 20;
    const rowsAll = drillFilteredSorted;
    const pages = Math.max(1, Math.ceil(rowsAll.length / PAGE_SIZE));

    const statusToneMap = {
      paid:            { fill: [220, 252, 231], text: [6, 95, 70],   lbl: 'Paid' },
      partially_paid:  { fill: [254, 243, 199], text: [120, 53, 15], lbl: 'Partial' },
      partial:         { fill: [254, 243, 199], text: [120, 53, 15], lbl: 'Partial' },
      overdue:         { fill: [255, 228, 230], text: [136, 19, 55], lbl: 'Overdue' },
      unpaid:          { fill: [255, 228, 230], text: [136, 19, 55], lbl: 'Unpaid' },
      sent:            { fill: [219, 234, 254], text: [30, 64, 175], lbl: 'Sent' },
      draft:           { fill: [241, 245, 249], text: [51, 65, 85],  lbl: 'Draft' },
      void:            { fill: [241, 245, 249], text: [100, 116, 139], lbl: 'Void' },
      cn_cleared:      { fill: [237, 233, 254], text: [76, 29, 149], lbl: 'CN Cleared' },
      mixed:           { fill: [207, 250, 254], text: [22, 78, 99],  lbl: 'Mixed' },
      written_off:     { fill: [241, 245, 249], text: [51, 65, 85],  lbl: 'W/Off' },
    };

    for (let pg = 0; pg < pages; pg++) {
      doc.addPage();
      const startIdx = pg * PAGE_SIZE;
      const endIdx = Math.min(startIdx + PAGE_SIZE, rowsAll.length);
      const slice = rowsAll.slice(startIdx, endIdx);

      drawChrome(pg + 2, 'Invoice Details',
        asc(`${header}: ${scopeLabel}`),
        `${range.from}  to  ${range.to}`);

      let py = 88;
      doc.setFontSize(13); doc.setTextColor(15, 23, 42); doc.setFont(undefined, 'bold');
      doc.text('Invoice Details', M, py);
      doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
      doc.text(`(${startIdx + 1} - ${endIdx} of ${rowsAll.length})`, M + 105, py);
      py += 14;

      const cols = ['#', 'Invoice #', 'Date / Exp', 'Customer', 'Sales', 'Payments', 'Outstd.', 'Status'];
      // Remember which visible-row indices have CN applied — used by
      // didParseCell to paint a rose background so credit-note invoices
      // stand out (matches the on-screen rose left-border).
      const cnRowIdx = new Set();
      const woRowIdx = new Set();
      const body = slice.map((inv, i) => {
        if ((inv.cn_applied || 0) > 0) cnRowIdx.add(i);
        else if ((inv.write_off || 0) > 0) woRowIdx.add(i);
        const exp = inv.expected_payment_date;
        const orig = inv.original_expected_payment_date;
        const dateCell = exp
          ? `${inv.date}\n(Exp ${fmtDDMon(exp)}${orig && orig !== exp ? ' pushed)' : ')'}`
          : (inv.date || '-');
        return [
          String(startIdx + i + 1),
          inv.invoice_number || '-',
          dateCell,
          asc(inv.customer_name || ''),
          inr(inv.sales),
          inr(inv.payments),
          inr(inv.outstanding),
          normaliseStatus(inv),   // will be styled in didParseCell
        ];
      });

      // Page total for this slice
      const sliceTotals = slice.reduce((a, r) => ({
        sales: a.sales + (r.sales || 0),
        payments: a.payments + (r.payments || 0),
        outstanding: a.outstanding + (r.outstanding || 0),
      }), { sales: 0, payments: 0, outstanding: 0 });

      // Only show footer subtotal on non-final pages; last page uses grand total.
      const isLastPage = pg === pages - 1;
      const footer = isLastPage
        ? ['', 'GRAND TOTAL', `(${rowsAll.length} inv)`, '', inr(drillTotals.sales), inr(drillTotals.payments), inr(drillTotals.outstanding), '']
        : ['', `Page Total (${startIdx + 1} - ${endIdx})`, '', '', inr(sliceTotals.sales), inr(sliceTotals.payments), inr(sliceTotals.outstanding), ''];

      autoTable(doc, {
        startY: py,
        head: [cols],
        body,
        foot: [footer],
        margin: { left: M, right: M, bottom: 40 },
        styles: { fontSize: 8, cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.4, textColor: [15, 23, 42] },
        headStyles: { fillColor: [237, 233, 254], textColor: [76, 29, 149], fontStyle: 'bold', fontSize: 8, halign: 'left' },
        footStyles: {
          fillColor: isLastPage ? [124, 58, 237] : [241, 245, 249],
          textColor: isLastPage ? 255 : [15, 23, 42],
          fontStyle: 'bold', fontSize: 9,
        },
        alternateRowStyles: { fillColor: [252, 252, 253] },
        columnStyles: {
          0: { halign: 'right', cellWidth: 24 },
          1: { cellWidth: 82, fontStyle: 'bold' },
          2: { cellWidth: 62, fontSize: 7, textColor: [100, 116, 139] },
          3: { cellWidth: 'auto' },
          4: { halign: 'right', cellWidth: 56 },
          5: { halign: 'right', cellWidth: 56 },
          6: { halign: 'right', cellWidth: 56 },
          7: { halign: 'center', cellWidth: 58 },
        },
        didParseCell: (data) => {
          // Row-level highlight for CN-applied invoices (rose) and
          // write-off-only rows (slate) — matches the on-screen list.
          if (data.section === 'body') {
            if (cnRowIdx.has(data.row.index)) {
              data.cell.styles.fillColor = [255, 241, 242]; // rose-50
            } else if (woRowIdx.has(data.row.index)) {
              data.cell.styles.fillColor = [248, 250, 252]; // slate-50
            }
          }
          // Colour the status pill background/text
          if (data.section === 'body' && data.column.index === 7) {
            const s = statusToneMap[String(data.cell.raw).toLowerCase()] || statusToneMap.draft;
            data.cell.styles.fillColor = s.fill;
            data.cell.styles.textColor = s.text;
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.halign = 'center';
            data.cell.text = [s.lbl];
          }
          // Grey-tint expected-date second line — done via low fontSize + textColor
          if (data.section === 'foot') {
            data.cell.styles.halign = data.column.index === 1 ? 'right'
              : data.column.index >= 4 && data.column.index <= 6 ? 'right'
              : data.cell.styles.halign;
          }
        },
      });

      // Grand-total thank-you note on last page
      if (isLastPage) {
        const finalY = doc.lastAutoTable.finalY + 20;
        if (finalY < PH - 60) {
          doc.setFillColor(248, 250, 252); doc.setDrawColor(226, 232, 240);
          doc.roundedRect(M, finalY, CW, 42, 4, 4, 'FD');
          // Emerald badge with "TY" instead of a heart glyph (Helvetica
          // has no glyph for U+2661 and prints it as a garble character).
          doc.setFillColor(220, 252, 231);
          doc.roundedRect(M + 8, finalY + 8, 26, 26, 4, 4, 'F');
          doc.setTextColor(6, 95, 70); doc.setFontSize(11); doc.setFont(undefined, 'bold');
          doc.text('TY', M + 14, finalY + 26);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(30, 41, 59); doc.setFontSize(9); doc.setFont(undefined, 'bold');
          doc.text('Thank you for your continued support.', M + 44, finalY + 20);
          doc.setFont(undefined, 'normal'); doc.setTextColor(100, 116, 139); doc.setFontSize(8);
          doc.text('For any queries, please reach out to the accounts team.', M + 44, finalY + 33);
        }
      }
    }

    // ── Replace the {tp} placeholder on every page with the real total.
    const total = doc.internal.pages.length - 1;   // jsPDF stores a blank page[0]
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      // Overwrite the same footer position — draw a small white rect first
      doc.setFillColor(255, 255, 255);
      doc.rect(PW - M - 60, PH - 24, 60, 12, 'F');
      doc.text(`Page ${i} of ${total}`, PW - M, PH - 16, { align: 'right' });
    }

    const safe = (scopeLabel || 'drill').replace(/[^a-z0-9]+/gi, '_');
    doc.save(`revenue_report_${safe}_${range.from}_${range.to}.pdf`);
  };

  return (
    <div className="p-6 space-y-4" data-testid="revenue-report-page">
      {/* ─── Header ─── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Revenue Report</h1>
          <p className="text-xs text-slate-500 mt-1">Invoice-level Sales / Payments / Write-off / Credit Notes / Outstanding by {header}. Click any row for the invoice drilldown.</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Sync freshness pill + button — unified via the Books Analytics
              shared GlobalSyncPill (invoices → payments → CN → CRM leads,
              sequential + rate-limit safe). */}
          <GlobalSyncPill />
          <Button size="sm" variant="outline" className="h-8" onClick={() => setAbout(true)} data-testid="rev-about-btn"><Info className="w-3.5 h-3.5 mr-1.5" /> About</Button>
          {voidData.count > 0 && (
            <button onClick={() => setVoidOpen(true)} data-testid="rev-void-pill" className="h-8 flex items-center gap-1.5 px-2.5 rounded-md border border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs" title={`${voidData.count} void invoices worth ₹${Math.round(voidData.total_voided_value).toLocaleString('en-IN')} — excluded from the totals above.`}>
              <Ban className="w-3.5 h-3.5" /> <span className="font-medium">Void ({voidData.count})</span>
            </button>
          )}
          <div className="relative">
            <Button size="sm" variant="outline" className="h-8" onClick={() => setExportOpen((o) => !o)} data-testid="rev-export"><Download className="w-3.5 h-3.5 mr-1.5" /> Export <ChevronDown className="w-3 h-3 ml-1" /></Button>
            {exportOpen && (
              <div className="absolute right-0 top-9 z-20 w-40 rounded-md border border-slate-200 bg-white shadow-lg text-xs" data-testid="rev-export-menu">
                <button onClick={exportCsv} data-testid="rev-export-csv" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left"><FileText className="w-3.5 h-3.5" /> CSV</button>
                <button onClick={exportXlsx} data-testid="rev-export-xlsx" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left border-t border-slate-100"><FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" /> Excel (.xlsx)</button>
                <button onClick={exportPdf} data-testid="rev-export-pdf" className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 text-left border-t border-slate-100"><FileText className="w-3.5 h-3.5 text-rose-700" /> PDF</button>
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={load} disabled={loading} data-testid="rev-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Reload</Button>
        </div>
      </div>

      {/* ─── Filters ─── */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase text-slate-500 font-semibold">Range</span>
          {['this_month', 'last_month', 'this_fy', 'last_fy', 'ytd', 'month', 'custom'].map((p) => (
            <button key={p} onClick={() => setPreset(p)} className={`px-2 py-1 rounded-md text-[10px] font-medium border ${preset === p ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-testid={`rev-preset-${p}`}>{p === 'this_month' ? 'This month' : p === 'last_month' ? 'Last month' : p === 'this_fy' ? 'This FY' : p === 'last_fy' ? 'Last FY' : p === 'ytd' ? 'YTD' : p === 'month' ? 'Specific month' : 'Custom'}</button>
          ))}
          {preset === 'month' && (
            <select value={monthKey} onChange={(e) => setMonthKey(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2" data-testid="rev-month-select">
              {monthOpts.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          )}
          {preset === 'custom' && (
            <>
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="rev-custom-from" />
              <span className="text-slate-400">→</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="rev-custom-to" />
            </>
          )}
          <span className="text-[10px] text-slate-400 ml-2">{range.label} · {range.from} → {range.to}</span>
          <div className="ml-auto flex items-center gap-2">
            <SpFilter all={salespersons} state={spState} setState={setSpState} />
            <button
              type="button"
              onClick={() => setExcludeRejected(!excludeRejected)}
              data-testid="rev-toggle-rejected"
              title="Rejected invoices are excluded by default so reports show clean actual sales"
              className={`text-[11px] px-2.5 py-1.5 rounded-md border transition whitespace-nowrap ${excludeRejected ? 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium' : 'bg-amber-50 border-amber-300 text-amber-700 font-medium'}`}
            >
              {excludeRejected ? '✓ Rejected excluded' : '⚠ Rejected included'}
            </button>
            <button type="button" onClick={openRejectedDrill} data-testid="rev-view-rejected" className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 whitespace-nowrap">View rejected</button>
            <Input placeholder="Customer…" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="h-8 w-40 text-xs" data-testid="rev-cust-search" />
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs px-2" data-testid="rev-status">
              {STATUS_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </Card>

      <SavedFilters
        storageKey="revenueReportSavedFilters"
        getCurrent={() => ({ preset, monthKey, customFrom, customTo, customerSearch, status, spState, excludeRejected })}
        onApply={(f) => {
          if (f.preset) setPreset(f.preset);
          if (f.monthKey) setMonthKey(f.monthKey);
          setCustomFrom(f.customFrom ?? ''); setCustomTo(f.customTo ?? '');
          setCustomerSearch(f.customerSearch ?? ''); setStatus(f.status ?? 'all');
          setSpState(f.spState ?? { op: 'is in', selected: [] });
          setExcludeRejected(f.excludeRejected ?? true);
        }}
      />

      {/* ─── KPIs (only for aggregate tabs) ─── */}
      {tab !== 'credit_note' ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <KPI icon={IndianRupee} label="Sales" value={fmtInr(t.sales)} tone="violet" onClick={() => openKpiDrill('sales')} testId="rev-kpi-sales" />
          <KPI icon={Wallet} label="Payments" value={fmtInr(t.payments)} tone="emerald" onClick={() => openKpiDrill('payments')} testId="rev-kpi-payments" />
          <KPI icon={Eraser} label="Write-off" value={fmtInr(t.write_off)} tone="slate" onClick={() => openKpiDrill('write_off')} testId="rev-kpi-writeoff" />
          <KPI icon={FileWarning} label="CN Applied" value={fmtInr(t.cn_applied)} tone="rose" onClick={() => openKpiDrill('cn')} testId="rev-kpi-cn" />
          <KPI icon={AlertTriangle} label="Outstanding" value={fmtInr(t.outstanding)} tone="amber" onClick={() => openKpiDrill('outstanding')} testId="rev-kpi-outstanding" />
          <KPI icon={TrendingUp} label="Collection %" value={`${t.collection_pct || 0}%`} tone="emerald" onClick={() => openKpiDrill('collection')} testId="rev-kpi-collection" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI icon={FileWarning} label="CN Invoices" value={cnData?.totals?.invoices ?? 0} tone="slate" />
          <KPI icon={IndianRupee} label="Total CN Applied" value={fmtInr(cnData?.totals?.cn_applied)} tone="rose" />
          <KPI icon={Eraser} label="Write-off (of these)" value={fmtInr(cnData?.totals?.write_off)} tone="slate" />
          <KPI icon={IndianRupee} label="Original Sales of these" value={fmtInr(cnData?.totals?.sales)} tone="violet" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">{TABS.map((x) => <TabsTrigger key={x.id} value={x.id} data-testid={`rev-tab-${x.id}`} className="data-[state=active]:bg-white data-[state=active]:shadow text-xs">{x.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      {/* ─── Table: aggregate tabs ─── */}
      {tab !== 'credit_note' ? (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-8 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            <div>{header}</div>
            {[{ k: 'invoices', l: '#' }, { k: 'sales', l: 'Sales' }, { k: 'payments', l: 'Payments' }, { k: 'write_off', l: 'Write-off' }, { k: 'cn_applied', l: 'CN Applied' }, { k: 'outstanding', l: 'Outstanding' }, { k: 'collection_pct', l: 'Coll %' }].map(({ k, l }) => (
              <button key={k} onClick={() => toggleSort(k)} className={`text-right hover:text-violet-700 ${sort.col === k ? 'text-violet-700' : ''}`}>{l}{sort.col === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</button>
            ))}
          </div>
          {loading ? (
            <div className="p-8 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : sortedRows.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">No data in this range.</div>
          ) : sortedRows.map((r) => (
            <div key={r.group} onClick={() => openDrill(r.group)} className="grid grid-cols-8 gap-2 px-3 py-2.5 border-t border-slate-100 text-xs items-center hover:bg-violet-50 cursor-pointer" data-testid={`rev-row-${r.group}`}>
              <div className="font-medium text-slate-800 truncate">{r.group}</div>
              <div className="text-right tabular-nums">{r.invoices}</div>
              <div className="text-right tabular-nums font-semibold text-violet-700">{fmtInr(r.sales)}</div>
              <div className="text-right tabular-nums text-emerald-700">{fmtInr(r.payments)}</div>
              <div className={`text-right tabular-nums ${r.write_off > 0 ? 'text-slate-700' : 'text-slate-300'}`}>{fmtInr(r.write_off)}</div>
              <div className={`text-right tabular-nums ${r.cn_applied > 0 ? 'text-rose-700' : 'text-slate-300'}`}>{fmtInr(r.cn_applied)}</div>
              <div className="text-right tabular-nums text-amber-700">{fmtInr(r.outstanding)}</div>
              <div className="text-right tabular-nums text-slate-500">{r.collection_pct}%</div>
            </div>
          ))}
          {sortedRows.length > 0 && (
            <div className="grid grid-cols-8 gap-2 px-3 py-2.5 border-t-2 border-slate-300 bg-slate-50 text-xs items-center font-bold">
              <div className="text-slate-800 flex items-center gap-2">
                GRAND TOTAL
                <ReconBadge sales={t.sales} payments={t.payments} write_off={t.write_off} cn_applied={t.cn_applied} outstanding={t.outstanding} testId="rev-recon-grand" />
              </div>
              <div className="text-right tabular-nums">{t.invoices}</div>
              <div className="text-right tabular-nums text-violet-700">{fmtInr(t.sales)}</div>
              <div className="text-right tabular-nums text-emerald-700">{fmtInr(t.payments)}</div>
              <div className="text-right tabular-nums text-slate-700">{fmtInr(t.write_off)}</div>
              <div className="text-right tabular-nums text-rose-700">{fmtInr(t.cn_applied)}</div>
              <div className="text-right tabular-nums text-amber-700">{fmtInr(t.outstanding)}</div>
              <div className="text-right tabular-nums">{t.collection_pct}%</div>
            </div>
          )}
        </Card>
      ) : (
        /* ─── Table: By Credit Note tab ─── */
        <Card className="overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            <div className="col-span-2">Invoice</div><div>Date</div><div className="col-span-2">Customer</div><div>Category</div>
            <div className="text-right">Sales</div><div className="text-right">Payments</div><div className="text-right">Write-off</div><div className="text-right">CN Applied</div><div className="text-right">Outstanding</div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : (cnData?.invoices?.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">No invoices with CN applied in this range.</div>
          ) : cnData.invoices.map((inv) => (
            <a key={inv.invoice_id} href={zohoUrl(inv.invoice_id)} target="_blank" rel="noreferrer" className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 text-xs items-center hover:bg-rose-50" data-testid={`rev-cn-row-${inv.invoice_id}`}>
              <div className="col-span-2 font-medium text-violet-700 whitespace-nowrap">{inv.invoice_number || '—'} ↗</div>
              <div className="text-slate-500">{inv.date}</div>
              <div className="col-span-2 text-slate-700 truncate">{inv.customer_name}</div>
              <div className="text-slate-500 truncate">{inv.cf_branch_name}</div>
              <div className="text-right tabular-nums font-medium">{fmtInr(inv.sales)}</div>
              <div className="text-right tabular-nums text-emerald-700">{fmtInr(inv.payments)}</div>
              <div className={`text-right tabular-nums ${inv.write_off > 0 ? 'text-slate-700' : 'text-slate-300'}`}>{fmtInr(inv.write_off)}</div>
              <div className="text-right tabular-nums font-semibold text-rose-700">{fmtInr(inv.cn_applied)}</div>
              <div className={`text-right tabular-nums ${inv.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(inv.outstanding)}</div>
            </a>
          ))}
          {(cnData?.invoices?.length ?? 0) > 0 && (
            <div className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t-2 border-slate-300 bg-slate-50 text-xs items-center font-bold">
              <div className="text-slate-800 col-span-6 flex items-center gap-2 flex-wrap">
                GRAND TOTAL ({cnData.totals.invoices} invoices)
                <ReconBadge sales={cnData.totals.sales} payments={(cnData.invoices || []).reduce((a, r) => a + (r.payments || 0), 0)} write_off={cnData.totals.write_off} cn_applied={cnData.totals.cn_applied} outstanding={(cnData.invoices || []).reduce((a, r) => a + (r.outstanding || 0), 0)} testId="rev-cn-recon" />
              </div>
              <div className="text-right tabular-nums text-violet-700">{fmtInr(cnData.totals.sales)}</div>
              <div />
              <div className="text-right tabular-nums text-slate-700">{fmtInr(cnData.totals.write_off)}</div>
              <div className="text-right tabular-nums text-rose-700">{fmtInr(cnData.totals.cn_applied)}</div>
              <div />
            </div>
          )}
        </Card>
      )}

      {/* ─── Drilldown modal ─── */}
      <Dialog open={drill.open} onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="rev-drill-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <div>
                <DialogTitle className="text-base">{drill.title || `${header}: ${drill.group_value}`}</DialogTitle>
                <DialogDescription className="text-[11px] text-slate-500">{drill.invoices.length} invoices · {range.from} → {range.to}</DialogDescription>
              </div>
              <Button
                size="sm" variant="outline"
                className="h-7 gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
                onClick={exportDrillPdf}
                disabled={drill.loading || drill.invoices.length === 0}
                data-testid="rev-drill-pdf"
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="flex items-center gap-2 px-1 pb-2 border-b border-slate-100 flex-wrap">
            <Filter className="w-3 h-3 text-slate-400" />
            <button onClick={toggleCnOnly} data-testid="rev-drill-cnonly" className={`text-[11px] px-2 py-1 rounded-md border ${drill.cnOnly ? 'bg-rose-50 border-rose-200 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {drill.cnOnly ? '✓ Only CN > 0' : 'Show only invoices with CN > 0'}
            </button>
            <span className="w-px h-4 bg-slate-200 mx-1" />
            <Layers className="w-3 h-3 text-slate-400" />
            <span className="text-[10px] text-slate-500">Group by</span>
            {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client', 'Client'], ['client_type', 'Client Type'], ['cn', 'Credit Note']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => { setDrillGroup(v); setDrillExpanded({}); }} data-testid={`rev-drill-group-${v}`}
                className={`text-[11px] px-2 py-1 rounded-md border transition ${drillGroup === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
            ))}
            {drillGroup !== 'none' && (
              <>
                <span className="w-px h-4 bg-slate-200 mx-1" />
                <span className="text-[10px] text-slate-500">Then by</span>
                {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client', 'Client'], ['client_type', 'Client Type'], ['cn', 'Credit Note']].filter(([v]) => v === 'none' || v !== drillGroup).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => { setDrillGroup2(v); setDrillExpanded({}); }} data-testid={`rev-drill-group2-${v}`}
                    className={`text-[11px] px-2 py-1 rounded-md border transition ${drillGroup2 === v ? 'bg-violet-500 text-white border-violet-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
                ))}
              </>
            )}
            {drillGroup !== 'none' && <span className="text-[10px] text-slate-400">click a group to expand</span>}
          </div>

          {/* ─── KPI strip (multi-select filter, computed from unfiltered invoices) ─── */}
          {!drill.loading && drill.invoices.length > 0 && (() => {
            const moneyDefs = [
              ['payments',    'Payments received', 'emerald', Wallet],
              ['cn',          'CN applied',        'rose',    FileWarning],
              ['write_off',   'Write-off',         'slate',   Eraser],
              ['outstanding', 'Outstanding',       'amber',   AlertTriangle],
            ];
            const statusDefs = [
              ['paid',        'Paid (cash)',   'emerald'],
              ['partial',     'Partially paid','amber'],
              ['unpaid',      'Unpaid / Sent', 'sky'],
              ['overdue',     'Overdue',       'rose'],
              ['cn_cleared',  'CN Cleared',    'violet'],
              ['mixed',       'Mixed (cash + CN)', 'amber'],
              ['written_off', 'Written off',   'slate'],
              ['draft',       'Draft',         'slate'],
              ['other',       'Other',         'slate'],
            ];
            const toneMap = {
              emerald: { on: 'bg-emerald-600 text-white border-emerald-600', off: 'bg-emerald-50/50 border-emerald-200 text-emerald-900 hover:bg-emerald-50' },
              rose:    { on: 'bg-rose-600 text-white border-rose-600',       off: 'bg-rose-50/50 border-rose-200 text-rose-900 hover:bg-rose-50' },
              slate:   { on: 'bg-slate-700 text-white border-slate-700',     off: 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100' },
              amber:   { on: 'bg-amber-600 text-white border-amber-600',     off: 'bg-amber-50/50 border-amber-200 text-amber-900 hover:bg-amber-50' },
              sky:     { on: 'bg-sky-600 text-white border-sky-600',         off: 'bg-sky-50/50 border-sky-200 text-sky-900 hover:bg-sky-50' },
              violet:  { on: 'bg-violet-600 text-white border-violet-600',   off: 'bg-violet-50/50 border-violet-200 text-violet-900 hover:bg-violet-50' },
            };
            const visibleStatus = statusDefs.filter(([k]) => drillKpis.status[k].count > 0);
            return (
              <div className="pb-3 border-b border-slate-100 space-y-2" data-testid="rev-drill-kpi-strip">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    Money · click to filter
                    <span
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold cursor-help"
                      title={
                        "Amounts add up to Sales:\n" +
                        "  Sales = Payments + CN + Write-off + Outstanding\n\n" +
                        "But invoice COUNTS don't add up here — one invoice can\n" +
                        "sit in more than one bucket at once. Example: a partially-\n" +
                        "paid invoice has Payments > 0 AND Outstanding > 0, so it\n" +
                        "gets counted in both cards. That's why 122 + 10 + 2 + 46\n" +
                        "can be greater than the 123 total invoices.\n\n" +
                        "If you need invoice counts that add up to 100%, use the\n" +
                        "Status row below — one invoice = exactly one status bucket."
                      }
                    >i</span>
                  </span>
                  {hasKpiFilter && (
                    <button
                      type="button"
                      onClick={clearKpiFilters}
                      data-testid="rev-drill-kpi-clear"
                      className="text-[11px] px-2 py-0.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear · {drillFiltered.length} / {drill.invoices.length}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {/* Total Sales — informational anchor for the row */}
                  <div
                    className="rounded-lg border border-violet-300 bg-violet-600 text-white px-3 py-2"
                    data-testid="rev-drill-kpi-sales"
                  >
                    <div className="text-[9px] font-bold uppercase tracking-wide opacity-90 flex items-center gap-1">
                      <IndianRupee className="w-3 h-3" /> Total Sales
                    </div>
                    <div className="text-[14px] font-bold tabular-nums mt-0.5">{fmtInr(drillKpis.sales)}</div>
                    <div className="text-[10px] opacity-80">{drillKpis.total_invoices} inv · 100%</div>
                  </div>
                  {moneyDefs.map(([k, label, tone, Icon]) => {
                    const v = drillKpis.money[k];
                    const active = drillMoneyKpis.has(k);
                    const t = toneMap[tone];
                    const pct = drillKpis.sales > 0 ? (100 * v.amount / drillKpis.sales) : 0;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => toggleMoneyKpi(k)}
                        disabled={v.count === 0}
                        data-testid={`rev-drill-kpi-money-${k}`}
                        className={`rounded-lg border px-3 py-2 text-left transition disabled:opacity-40 disabled:cursor-not-allowed ${active ? t.on : t.off}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-[9px] font-bold uppercase tracking-wide opacity-80 flex items-center gap-1">
                            <Icon className="w-3 h-3" /> {label}
                          </div>
                          {active && <X className="w-3 h-3 opacity-80" />}
                        </div>
                        <div className="text-[14px] font-bold tabular-nums mt-0.5">{fmtInr(v.amount)}</div>
                        <div className="text-[10px] opacity-70">{v.count} inv · <span className="font-semibold">{pct.toFixed(1)}%</span></div>
                      </button>
                    );
                  })}
                </div>
                {visibleStatus.length > 0 && (
                  <>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 pt-1">Status · click to filter</div>
                    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                      {visibleStatus.map(([k, label, tone]) => {
                        const v = drillKpis.status[k];
                        const active = drillStatusKpis.has(k);
                        const t = toneMap[tone];
                        const pct = drillKpis.sales > 0 ? (100 * v.amount / drillKpis.sales) : 0;
                        // Non-zero money-flow components — helps user reconcile
                        // this bucket's sales against the Money row above.
                        const parts = [];
                        if (v.cash > 0.5)        parts.push(`${fmtInr(v.cash)} cash`);
                        if (v.cn > 0.5)          parts.push(`${fmtInr(v.cn)} CN`);
                        if (v.write_off > 0.5)   parts.push(`${fmtInr(v.write_off)} w/off`);
                        if (v.outstanding > 0.5) parts.push(`${fmtInr(v.outstanding)} due`);
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => toggleStatusKpi(k)}
                            data-testid={`rev-drill-kpi-status-${k}`}
                            className={`rounded-lg border px-3 py-2 text-left transition ${active ? t.on : t.off}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-[9px] font-bold uppercase tracking-wide opacity-80">{label}</div>
                              {active && <X className="w-3 h-3 opacity-80" />}
                            </div>
                            <div className="text-[14px] font-bold tabular-nums mt-0.5">{v.count}</div>
                            <div className="text-[10px] opacity-70">{fmtInr(v.amount)} · <span className="font-semibold">{pct.toFixed(1)}%</span></div>
                            {parts.length > 0 && (
                              <div className="text-[9px] opacity-70 mt-0.5 truncate" title={parts.join(' + ')}>
                                = {parts.join(' + ')}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {drill.loading ? (
              <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-violet-600" /></div>
            ) : (
              <div className="border border-slate-200 rounded">
                {(() => {
                  const SortHead = ({ col, className = '', children, align = 'left' }) => {
                    const active = drillSort.col === col && drillSort.dir;
                    const arrow = active ? (drillSort.dir === 'asc' ? '▲' : '▼') : '';
                    const canSort = drillGroups === null;
                    return canSort ? (
                      <button type="button" onClick={() => toggleDrillSort(col)}
                        className={`${className} ${align === 'right' ? 'text-right' : 'text-left'} flex items-center gap-0.5 ${align === 'right' ? 'justify-end' : ''} hover:text-slate-800 transition`}
                        data-testid={`rev-drill-sort-${col}`}>
                        {children}
                        {active && <span className="text-[8px] text-slate-700">{arrow}</span>}
                      </button>
                    ) : <div className={`${className} ${align === 'right' ? 'text-right' : ''}`}>{children}</div>;
                  };
                  return (
                    <div className="grid gap-2 px-3 py-1.5 bg-slate-100/95 backdrop-blur text-[9px] uppercase tracking-wider text-slate-500 font-medium sticky top-0 z-10 shadow-sm border-b border-slate-200 rounded-t"
                         style={{ gridTemplateColumns: '36px repeat(12, minmax(0, 1fr))' }}>
                      <div className="text-right">#</div>
                      <SortHead col="invoice_number" className="col-span-2">Inv #</SortHead>
                      <SortHead col="date">Date / Exp</SortHead>
                      <SortHead col="customer_name" className="col-span-2">Customer</SortHead>
                      <SortHead col="salesperson_name">Salesperson</SortHead>
                      <SortHead col="sales" align="right">Sales</SortHead>
                      <SortHead col="payments" align="right">Payments</SortHead>
                      <SortHead col="write_off" align="right">Write-off</SortHead>
                      <SortHead col="cn_applied" align="right">CN</SortHead>
                      <SortHead col="outstanding" align="right">Outstanding</SortHead>
                      <SortHead col="status">Status</SortHead>
                    </div>
                  );
                })()}
                {(() => {
                  let slNo = 0;
                  const row = (inv, keySuffix = '') => {
                    slNo += 1;
                    return (
                    <button
                      key={inv.invoice_id + keySuffix}
                      type="button"
                      onClick={() => setInvoiceDetailId(inv.invoice_id)}
                      className={`w-full grid gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-violet-50 text-left ${
                        (inv.cn_applied || 0) > 0 ? 'bg-rose-50/50 border-l-2 border-l-rose-400 pl-[10px]' :
                        (inv.write_off || 0) > 0 ? 'bg-slate-50 border-l-2 border-l-slate-400 pl-[10px]' : ''
                      }`}
                      style={{ gridTemplateColumns: '36px repeat(12, minmax(0, 1fr))' }}
                      data-testid={`rev-drill-row-${inv.invoice_id}`}
                    >
                      <div className="text-right text-slate-400 tabular-nums">{slNo}</div>
                      <div className="col-span-2 font-medium text-violet-700 whitespace-nowrap">{inv.invoice_number || '—'}</div>
                      <div className="text-slate-500 leading-tight">
                        <div>{inv.date}</div>
                        {inv.expected_payment_date && (() => {
                          const exp = inv.expected_payment_date;
                          const orig = inv.original_expected_payment_date;
                          const pushed = orig && orig !== exp;
                          const shortExp = new Date(exp + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                          return (
                            <div
                              className={`text-[9px] font-medium ${pushed ? 'text-amber-600' : 'text-emerald-600'}`}
                              title={`Expected ${exp}${pushed ? ` · originally ${orig}` : ''}`}
                            >
                              Exp {shortExp}{pushed ? ' ⇡' : ''}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="col-span-2 text-slate-700 truncate">{inv.customer_name}</div>
                      <div className="text-slate-500 truncate">{inv.salesperson_name || '—'}</div>
                      <div className="text-right tabular-nums font-medium">{fmtInr(inv.sales)}</div>
                      <div className="text-right tabular-nums text-emerald-700">{fmtInr(inv.payments)}</div>
                      <div className={`text-right tabular-nums ${inv.write_off > 0 ? 'text-slate-700 font-medium' : 'text-slate-300'}`}>{fmtInr(inv.write_off)}</div>
                      <div className={`text-right tabular-nums ${inv.cn_applied > 0 ? 'text-rose-700 font-medium' : 'text-slate-300'}`}>{fmtInr(inv.cn_applied)}</div>
                      <div className={`text-right tabular-nums font-medium ${inv.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(inv.outstanding)}</div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-slate-100 text-slate-700 text-[9px] py-0">{inv.status}</Badge>
                        {(inv.cn_applied || 0) > 0 && <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[8px] py-0 px-1 font-semibold">CN</Badge>}
                      </div>
                    </button>
                    );
                  };
                  return drillGroups === null
                    ? drillFilteredSorted.map((inv) => row(inv))
                    : drillGroups.map((g) => (
                      <div key={g.key}>
                        <button
                          type="button"
                          onClick={() => setDrillExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
                          className="w-full grid gap-2 px-3 py-1.5 bg-violet-50/70 border-t border-violet-100 text-[11px] items-center font-semibold text-left hover:bg-violet-100/70 transition cursor-pointer"
                          style={{ gridTemplateColumns: '36px repeat(12, minmax(0, 1fr))' }}
                          data-testid={`rev-drill-grp-${g.key}`}
                        >
                          <div />
                          <div className="col-span-6 text-violet-900 truncate flex items-center gap-1">
                            <ChevronDown className={`w-3 h-3 shrink-0 text-violet-500 transition-transform ${drillExpanded[g.key] ? '' : '-rotate-90'}`} />
                            {g.key} <span className="text-[10px] font-normal text-slate-500">· {g.invoices.length} inv</span>
                          </div>
                          <div className="text-right tabular-nums text-violet-800">{fmtInr(g.sales)}</div>
                          <div className="text-right tabular-nums text-emerald-700">{fmtInr(g.payments)}</div>
                          <div className={`text-right tabular-nums ${g.write_off > 0 ? 'text-slate-700' : 'text-slate-300'}`}>{fmtInr(g.write_off)}</div>
                          <div className={`text-right tabular-nums ${g.cn > 0 ? 'text-rose-700' : 'text-slate-300'}`}>{fmtInr(g.cn)}</div>
                          <div className={`text-right tabular-nums ${g.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(g.outstanding)}</div>
                          <div />
                        </button>
                        {drillExpanded[g.key] && (g.subs.length > 0
                          ? g.subs.map((s2) => {
                            const sk = `${g.key}||${s2.key}`;
                            return (
                              <div key={sk}>
                                <button
                                  type="button"
                                  onClick={() => setDrillExpanded((p) => ({ ...p, [sk]: !p[sk] }))}
                                  className="w-full grid gap-2 px-3 py-1 bg-violet-50/40 border-t border-violet-50 text-[11px] items-center font-medium text-left hover:bg-violet-100/50 transition cursor-pointer"
                                  style={{ gridTemplateColumns: '36px repeat(12, minmax(0, 1fr))' }}
                                  data-testid={`rev-drill-subgrp-${sk}`}
                                >
                                  <div />
                                  <div className="col-span-6 text-violet-800 truncate flex items-center gap-1 pl-5">
                                    <ChevronDown className={`w-3 h-3 shrink-0 text-violet-400 transition-transform ${drillExpanded[sk] ? '' : '-rotate-90'}`} />
                                    {s2.key} <span className="text-[10px] font-normal text-slate-500">· {s2.invoices.length} inv</span>
                                  </div>
                                  <div className="text-right tabular-nums text-violet-700">{fmtInr(s2.sales)}</div>
                                  <div className="text-right tabular-nums text-emerald-700">{fmtInr(s2.payments)}</div>
                                  <div className={`text-right tabular-nums ${s2.write_off > 0 ? 'text-slate-700' : 'text-slate-300'}`}>{fmtInr(s2.write_off)}</div>
                                  <div className={`text-right tabular-nums ${s2.cn > 0 ? 'text-rose-700' : 'text-slate-300'}`}>{fmtInr(s2.cn)}</div>
                                  <div className={`text-right tabular-nums ${s2.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(s2.outstanding)}</div>
                                  <div />
                                </button>
                                {drillExpanded[sk] && s2.invoices.map((inv) => row(inv, `-${sk}`))}
                              </div>
                            );
                          })
                          : g.invoices.map((inv) => row(inv, `-${g.key}`)))}
                      </div>
                    ));
                })()}
                <div className="grid gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-[11px] items-center font-bold"
                     style={{ gridTemplateColumns: '36px repeat(12, minmax(0, 1fr))' }} data-testid="rev-drill-total-row">
                  <div />
                  <div className="col-span-6 text-slate-800 flex items-center gap-2 flex-wrap">
                    GRAND TOTAL <span className="text-[10px] font-normal text-slate-500">· {drillFiltered.length}{hasKpiFilter ? ` / ${drill.invoices.length}` : ''} inv</span>
                    <ReconBadge sales={drillTotals.sales} payments={drillTotals.payments} write_off={drillTotals.write_off} cn_applied={drillTotals.cn} outstanding={drillTotals.outstanding} testId="rev-drill-recon" />
                  </div>
                  <div className="text-right tabular-nums text-violet-700">{fmtInr(drillTotals.sales)}</div>
                  <div className="text-right tabular-nums text-emerald-700">{fmtInr(drillTotals.payments)}</div>
                  <div className="text-right tabular-nums text-slate-700">{fmtInr(drillTotals.write_off)}</div>
                  <div className="text-right tabular-nums text-rose-700">{fmtInr(drillTotals.cn)}</div>
                  <div className={`text-right tabular-nums ${drillTotals.outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(drillTotals.outstanding)}</div>
                  <div />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Per-invoice detail modal (opened from any drill row) ─── */}
      <InvoiceDetailModal
        open={!!invoiceDetailId}
        onClose={() => setInvoiceDetailId(null)}
        invoiceId={invoiceDetailId}
      />

      {/* ─── Voided invoices modal ─── */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="rev-void-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Ban className="w-4 h-4 text-slate-500" /> Voided invoices ({voidData.count})</DialogTitle>
            <DialogDescription className="text-[11px] text-slate-500">
              These invoices were cancelled in Zoho Books. Their total original value was <b>₹{Math.round(voidData.total_voided_value).toLocaleString('en-IN')}</b>. They are excluded from the Revenue Report totals and every group breakdown (Category / Salesperson / Client Type / Customer / Credit Note) so the numbers reflect only real business activity.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="grid grid-cols-8 gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium sticky top-0">
                <div className="col-span-2">Invoice</div><div>Date</div><div className="col-span-2">Customer</div><div>Category</div><div>Salesperson</div><div className="text-right">Original Value</div>
              </div>
              {voidData.invoices.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400">No voided invoices in this range.</div>
              ) : voidData.invoices.map((inv) => (
                <a key={inv.invoice_id} href={zohoUrl(inv.invoice_id)} target="_blank" rel="noreferrer" className="grid grid-cols-8 gap-2 px-3 py-2 border-t border-slate-100 text-xs items-center hover:bg-slate-50" data-testid={`rev-void-row-${inv.invoice_id}`}>
                  <div className="col-span-2 font-medium text-violet-700 whitespace-nowrap">{inv.invoice_number || '—'} ↗</div>
                  <div className="text-slate-500">{inv.date}</div>
                  <div className="col-span-2 text-slate-700 truncate">{inv.customer_name}</div>
                  <div className="text-slate-500 truncate">{inv.cf_branch_name}</div>
                  <div className="text-slate-500 truncate">{inv.salesperson_name || '—'}</div>
                  <div className="text-right tabular-nums text-slate-400 line-through">₹{Math.round(inv.voided_value).toLocaleString('en-IN')}</div>
                </a>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── About / Explanation popover ─── */}
      <Dialog open={about} onOpenChange={setAbout}>
        <DialogContent className="max-w-2xl" data-testid="rev-about-dialog">
          <DialogHeader>
            <DialogTitle>About this report</DialogTitle>
            <DialogDescription>How the numbers are computed and where they come from.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs text-slate-700 leading-relaxed">
            <div>
              <p className="font-semibold text-slate-900 mb-1">Purpose</p>
              <p>Rupee-perfect reconciliation of your Zoho Books invoices — how much you billed, how much you actually collected, how much you knocked off via credit notes, and how much is still outstanding — sliced by Category, Salesperson, Client Type, Customer, or Credit Note.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Where the data comes from</p>
              <p>Pulled from your <b>zoho_invoices</b> cache which mirrors Zoho Books. Category = Zoho custom field <code>cf_branch_name</code>. Sales = <code>bcy_total</code>. Payments = <code>payment_made</code>. Write-off = <code>write_off_amount</code>. Outstanding = <code>bcy_balance</code>. <b>CN Applied = Sales − Payments − Write-off − Outstanding</b> (money knocked off the invoice via credit notes, isolated from write-offs).</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">The reconciliation identity</p>
              <p className="font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1.5 inline-block">Sales = Payments + Write-off + CN Applied + Outstanding</p>
              <p className="mt-1 text-slate-500">holds row-by-row and at the grand total.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Write-off vs CN Applied</p>
              <p><b>Write-off</b> is a small residual you explicitly forgive in Zoho (e.g. rounding, TDS gap) — it comes from Zoho&apos;s <code>write_off_amount</code>. <b>CN Applied</b> is money knocked off by an actual credit note. The old report lumped both together; they are now separate columns so you can reconcile each independently.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Freshness / Zoho sync</p>
              <p>The colored pill next to <b>About</b> shows how fresh the cache is (green &lt; 15 min · amber &lt; 1 hr · red &gt; 1 hr). Click it to run an incremental sync — it uses Zoho&apos;s <code>last_modified_time</code> filter so only invoices that changed since the last sync are pulled. Typical cost: <b>1-3 API calls</b>. First-ever sync: 20-40. There is a 30-second cooldown between manual syncs.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Credit Notes</p>
              <p>CN Applied is computed <b>per invoice</b> from the reconciliation identity above. Use the <b>By Credit Note</b> tab to see every invoice with CN &gt; 0 in the current window, sorted by CN amount. Inside any drilldown modal, use the &quot;Show only invoices with CN &gt; 0&quot; chip to audit specific accounts.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Voided invoices</p>
              <p>Invoices marked <b>void</b> in Zoho Books are <b>excluded</b> from every aggregation (Category / Salesperson / Client Type / Customer / Credit Note) so they never inflate Sales. They still show as a separate <span className="inline-flex items-center gap-1"><Ban className="w-3 h-3" /> Void (N)</span> pill in the header — click it to review the list. Voids typically represent cancellations, duplicates, or billing errors and should be audited periodically.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">Drilldown</p>
              <p>Clicking any row opens the invoices behind it. Every invoice number is a link — it opens the invoice directly in Zoho Books in a new tab.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
